import React, { useId, useRef, useEffect } from "react";

export type DSTextareaSize = "sm" | "md";

export interface DSTextareaProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  "size" | "rows"
> {
  size?: DSTextareaSize;
  label?: string;
  error?: string;
  minRows?: number;
  maxRows?: number;
  dataTestId: string;
  ref?: React.Ref<HTMLTextAreaElement>;
}

export const DSTextarea = ({
  size = "sm",
  label,
  error,
  disabled = false,
  className = "",
  dataTestId,
  id: providedId,
  minRows = 3,
  maxRows,
  value,
  onChange,
  ref,
  ...props
}: DSTextareaProps) => {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to recalculate
    textarea.style.height = "auto";

    // Calculate new height based on scrollHeight
    const lineHeight = parseInt(
      window.getComputedStyle(textarea).lineHeight || "20",
    );
    const minHeight = minRows * lineHeight;
    const maxHeight = maxRows ? maxRows * lineHeight : Infinity;

    const newHeight = Math.min(
      Math.max(textarea.scrollHeight, minHeight),
      maxHeight,
    );

    textarea.style.height = `${newHeight}px`;
  }, [value, minRows, maxRows]);

  // Container styles
  const containerStyles = "flex flex-col gap-xs w-full";

  // Base textarea styles
  const baseTextareaStyles =
    "w-full rounded-standard bg-input text-primary font-body text-sm transition-all placeholder:text-placeholder resize-none overflow-hidden";

  // Size styles
  const sizeStyles = {
    sm: "px-md py-3 text-sm",
    md: "px-md py-3 text-base",
  };

  // State styles
  const stateStyles = disabled
    ? "opacity-80 cursor-not-allowed shadow-[0px_0px_10px_0px_var(--shadow-secondary)] text-placeholder border-2 border-input"
    : error
      ? "shadow-[0px_0px_10px_0px_var(--shadow-primary)] border-2 border-danger focus:shadow-[0px_0px_10px_0px_var(--shadow-focus)] focus:border-danger focus:outline-none"
      : "shadow-[0px_0px_10px_0px_var(--shadow-primary)] border-2 border-input focus:shadow-[0px_0px_10px_0px_var(--shadow-focus)] focus:border-brand focus:outline-none hover:shadow-[0px_0px_10px_0px_var(--shadow-focus)]";

  return (
    <div className={containerStyles}>
      {label && (
        <label
          htmlFor={id}
          className="text-xs text-secondary font-body"
        >
          {label}
        </label>
      )}
      <textarea
        ref={(node) => {
          textareaRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            (
              ref as React.MutableRefObject<HTMLTextAreaElement | null>
            ).current = node;
          }
        }}
        id={id}
        disabled={disabled}
        data-testid={dataTestId}
        data-intercom-target={dataTestId}
        value={value}
        onChange={onChange}
        className={`${baseTextareaStyles} ${sizeStyles[size]} ${stateStyles} ${className}`}
        {...props}
      />
      {error && !disabled && (
        <span className="text-xs text-danger">{error}</span>
      )}
    </div>
  );
};
